import { formatDistance } from "@nova/format"
import { createQuery } from "@nova/queryable"
import { useLoadingText } from "features/data-management/workspace/workspace/useLoadingText"
import { useMemo } from "react"
import { CardCollection, StatCard } from "../CardCollection"
import { Card } from "../MetricLayout"
import * as Icons from "@nova/component-icons"
import { useBatchPilotTrackingQuery } from "../PilotTrackingQueries"
import { PilotActivityGraph } from "../pilots/PilotActivityGraph"
import { useAppSelector } from "integrations/redux/hooks"
import { selectActivityTimeRange } from "../pilots/slice"
import { selectWorkspaceDistanceUnit } from "features/data-management/workspace/WorkspaceManagementSlice"

export function WorkspaceFlightTimeSection() {
    const range = useAppSelector(selectActivityTimeRange)
    const distanceUnit = useAppSelector(selectWorkspaceDistanceUnit)

    const startDate = useMemo(() => {
        if (range === "all") return null
        const start = new Date()
        start.setHours(0, 0, 0, 0)
        if (range === "30d") start.setDate(start.getDate() - 29)
        else if (range === "6m") start.setMonth(start.getMonth() - 6)
        else if (range === "1y") start.setFullYear(start.getFullYear() - 1)
        return start
    }, [range])

    const queries = useMemo(() => {
        const startedAt = startDate?.toISOString()
        return {
            numMissionsQuery: createQuery({
                resource: "missions",
                aggregates: [{ field: "id", func: "COUNT", alias: "numMissions" }],
                filters: startedAt
                    ? [{ field: "metadata.started_at", op: ">=", value: startedAt }]
                    : [],
            }),
            distanceFlownQuery: createQuery({
                resource: "missions",
                aggregates: [
                    { field: "metadata.distance_flown", func: "SUM", alias: "distanceFlown" },
                ],
                filters: startedAt
                    ? [{ field: "metadata.started_at", op: ">=", value: startedAt }]
                    : [],
            }),
            totalFlightTimeQuery: createQuery({
                resource: "missions",
                aggregates: [{ field: "metadata.duration", func: "SUM", alias: "totalFlightTime" }],
                filters: startedAt
                    ? [{ field: "metadata.started_at", op: ">=", value: startedAt }]
                    : [],
            }),
        }
    }, [startDate])

    const { data, isLoading } = useBatchPilotTrackingQuery(queries)
    const loadingText = useLoadingText(isLoading)
    const stats = useMemo(() => {
        if (isLoading || !data) {
            return [
                {
                    Icon: Icons.Controller,
                    title: "Total Hours",
                    body: loadingText,
                },
                {
                    Icon: Icons.Drone,
                    title: "Total Flights",
                    body: loadingText,
                },
                {
                    Icon: Icons.Route,
                    title: "Distance Flown",
                    body: loadingText,
                },
            ]
        }
        const numMissions = +data.numMissionsQuery[0].numMissions
        const distanceFlownMeters = +(data.distanceFlownQuery[0].distanceFlown ?? 0)
        const totalFlightTimeHrs = +(data.totalFlightTimeQuery[0].totalFlightTime ?? 0) / 3600
        return [
            {
                Icon: Icons.Controller,
                title: "Total Hours",
                body: `${totalFlightTimeHrs.toFixed(0)}`,
            },
            {
                Icon: Icons.Drone,
                title: "Total Flights",
                body: `${numMissions}`,
            },
            {
                Icon: Icons.Route,
                title: "Distance Flown",
                body: formatDistance({ value: distanceFlownMeters, unit: distanceUnit }),
            },
        ]
    }, [data, distanceUnit, isLoading, loadingText])
    return (
        <div className="flex flex-col gap-3">
            <div className="text-gray-50 text-[22px] telegraf">Workspace Flight Time</div>
            <Card className="bg-white-100">
                <div className="flex gap-6 items-stretch">
                    <div className="flex-1 min-w-0">
                        <PilotActivityGraph />
                    </div>
                    <div>
                        <CardCollection
                            orientation="vertical"
                            cards={stats.map((s) => (
                                <StatCard key={s.title} {...s} />
                            ))}
                        />
                    </div>
                </div>
            </Card>
        </div>
    )
}
